import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Shield, AlertTriangle, CheckCircle2, XCircle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SSLInspector() {
  const navigate = useNavigate();
  const [hostname, setHostname] = useState("");
  const [port, setPort] = useState("443");
  const [sslResult, setSslResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // CSR Generation
  const [commonName, setCommonName] = useState("");
  const [organization, setOrganization] = useState("");
  const [country, setCountry] = useState("US");
  const [state, setState] = useState("");
  const [locality, setLocality] = useState("");
  const [csrResult, setCsrResult] = useState("");

  const handleCheckSSL = async () => {
    if (!hostname.trim()) {
      toast.error("Please enter a hostname");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ssl/check`, {
        hostname,
        port: parseInt(port)
      });
      setSslResult(response.data);
      toast.success("Certificate checked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "SSL check failed");
      setSslResult(null);
    }
    setLoading(false);
  };

  const handleGenerateCSR = async () => {
    if (!commonName || !organization || !country) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ssl/generate-csr`, {
        common_name: commonName,
        organization,
        country,
        state,
        locality
      });
      setCsrResult(response.data.csr_commands);
      toast.success("CSR commands generated!");
    } catch (error) {
      toast.error("CSR generation failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getCertificateStatus = () => {
    if (!sslResult || !sslResult.valid) return null;
    
    if (sslResult.expired) {
      return { icon: XCircle, color: "text-red-500", text: "Certificate Expired", bgColor: "bg-red-500/10" };
    } else if (sslResult.expires_soon) {
      return { icon: AlertTriangle, color: "text-yellow-500", text: "Expires Soon", bgColor: "bg-yellow-500/10" };
    } else {
      return { icon: CheckCircle2, color: "text-emerald-500", text: "Valid Certificate", bgColor: "bg-emerald-500/10" };
    }
  };

  const status = getCertificateStatus();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>SSL/TLS Certificate Inspector</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <Tabs defaultValue="check" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-zinc-800 border-zinc-700 mb-6">
              <TabsTrigger value="check" data-testid="check-tab">Check Certificate</TabsTrigger>
              <TabsTrigger value="csr" data-testid="csr-tab">Generate CSR</TabsTrigger>
            </TabsList>

            {/* Check Certificate Tab */}
            <TabsContent value="check" className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="hostname" className="text-zinc-400 mb-2 block">Hostname</Label>
                  <Input
                    data-testid="hostname-input"
                    id="hostname"
                    value={hostname}
                    onChange={(e) => setHostname(e.target.value)}
                    placeholder="example.com or https://example.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="port" className="text-zinc-400 mb-2 block">Port</Label>
                  <Input
                    data-testid="port-input"
                    id="port"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="443"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="check-ssl-btn"
                onClick={handleCheckSSL}
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              >
                {loading ? "Checking..." : "Check Certificate"}
              </Button>

              {sslResult && sslResult.valid && status && (
                <div className="space-y-6">
                  {/* Status Banner */}
                  <div className={`${status.bgColor} border border-${status.color.replace('text-', '')} rounded-lg p-6`}>
                    <div className="flex items-center space-x-3">
                      <status.icon className={`w-8 h-8 ${status.color}`} />
                      <div>
                        <div className={`text-xl font-bold ${status.color}`}>{status.text}</div>
                        <div className="text-sm text-zinc-400 mt-1">
                          {sslResult.days_remaining} days remaining
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Certificate Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Common Name</div>
                          <div className="text-white font-mono">{sslResult.common_name}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Organization</div>
                          <div className="text-white">{sslResult.organization}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Issuer</div>
                          <div className="text-white">{sslResult.issuer}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Issuer Organization</div>
                          <div className="text-white">{sslResult.issuer_org}</div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Valid From</div>
                          <div className="text-white font-mono text-sm">{sslResult.valid_from}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Valid Until</div>
                          <div className="text-white font-mono text-sm">{sslResult.valid_until}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Serial Number</div>
                          <div className="text-white font-mono text-xs">{sslResult.serial_number}</div>
                        </div>
                        <div className="border-b border-zinc-700 pb-2">
                          <div className="text-sm text-zinc-400">Version</div>
                          <div className="text-white">{sslResult.version}</div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Security Info */}
                  <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Security Information</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400 mb-1">TLS Version</div>
                        <div className="text-lg font-bold text-white">{sslResult.tls_version}</div>
                      </div>
                      <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400 mb-1">Cipher Suite</div>
                        <div className="text-sm font-mono text-white break-all">{sslResult.cipher_suite}</div>
                      </div>
                      <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                        <div className="text-sm text-zinc-400 mb-1">Key Size</div>
                        <div className="text-lg font-bold text-white">{sslResult.key_size} bits</div>
                      </div>
                    </div>
                  </Card>

                  {/* SANs */}
                  {sslResult.san && sslResult.san.length > 0 && (
                    <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                      <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Subject Alternative Names (SAN)</h3>
                      <div className="flex flex-wrap gap-2">
                        {sslResult.san.map((name, index) => (
                          <span key={index} className="bg-zinc-900/50 px-3 py-1 rounded border border-zinc-700 text-sm text-white font-mono">
                            {name}
                          </span>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {sslResult && !sslResult.valid && (
                <Card className="bg-red-500/10 border-red-500 p-6">
                  <div className="flex items-center space-x-3">
                    <XCircle className="w-8 h-8 text-red-500" />
                    <div>
                      <div className="text-xl font-bold text-red-500">Certificate Invalid</div>
                      <div className="text-sm text-zinc-400 mt-1">{sslResult.error}</div>
                      {sslResult.details && (
                        <div className="text-xs text-zinc-500 mt-2">{sslResult.details}</div>
                      )}
                    </div>
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Generate CSR Tab */}
            <TabsContent value="csr" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cn" className="text-zinc-400 mb-2 block">Common Name (CN) *</Label>
                  <Input
                    data-testid="common-name-input"
                    id="cn"
                    value={commonName}
                    onChange={(e) => setCommonName(e.target.value)}
                    placeholder="example.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="org" className="text-zinc-400 mb-2 block">Organization *</Label>
                  <Input
                    data-testid="organization-input"
                    id="org"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="My Company Inc."
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-zinc-400 mb-2 block">Country Code *</Label>
                  <Input
                    data-testid="country-input"
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="US"
                    maxLength={2}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="state" className="text-zinc-400 mb-2 block">State/Province</Label>
                  <Input
                    data-testid="state-input"
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="California"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="locality" className="text-zinc-400 mb-2 block">City/Locality</Label>
                  <Input
                    data-testid="locality-input"
                    id="locality"
                    value={locality}
                    onChange={(e) => setLocality(e.target.value)}
                    placeholder="San Francisco"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="generate-csr-btn"
                onClick={handleGenerateCSR}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              >
                Generate CSR Commands
              </Button>

              {csrResult && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>OpenSSL Commands</h3>
                    <Button
                      data-testid="copy-csr-btn"
                      onClick={() => copyToClipboard(csrResult)}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-white"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-zinc-900 p-4 rounded border border-zinc-700 overflow-x-auto text-sm text-zinc-300">
                    {csrResult}
                  </pre>
                  <p className="text-sm text-zinc-500 mt-4">
                    Run these commands in your terminal to generate a private key and CSR.
                  </p>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}